<template>
    <div>
        <ModeSwitcher
            ref="modeSwitcher"
            :create-mode-text="`${$t('create')} ${toLower($t(`itineraryType.${selectedItinerary}`))} ${$t('component')}`"
            :edit-mode-text="`${$t('edit')} ${toLower($t(`itineraryType.${selectedItinerary}`))} ${$t('component')}`"
            @reset="editItineraryId = null"
        >
            <template v-slot:btn-block>
                <v-btn data-cy="create" color="primary" @click="isShowModal = true">
                    {{ $t('create') }}
                </v-btn>
            </template>
            <template v-slot:main-mode>
                <v-data-table-server
                    :headers="useHeaders"
                    :items="itinerary"
                    :items-per-page="-1"
                    :items-per-page-options="[-1]"
                    :loading="isLoading"
                    :items-length="itinerary.length"
                    disable-sort
                    density="compact"
                    class="elevation-1"
                    data-cy="table"
                >
                    <template v-slot:item.dayAction="{item, index}">
                        <v-icon
                            v-if="showDownIcon(index)"
                            :data-cy="`down-${index}`"
                            class="cursor-pointer"
                            start
                            @click="itineraryDaySwap(item.id, itinerary[index + 1].id)"
                        >
                            mdi-arrow-down-thin
                        </v-icon>
                        <v-icon
                            v-if="showUpIcon(index)"
                            :data-cy="`up-${index}`"
                            class="cursor-pointer"
                            start
                            @click="itineraryDaySwap(itinerary[index - 1].id, item.id)"
                        >
                            mdi-arrow-up-thin
                        </v-icon>
                    </template>
                    <template v-slot:item.name="{item}">
                        <div class="text-link" @click="editItinerary(item)">
                            {{ item.name }}
                        </div>
                    </template>
                    <template v-slot:item.productName="{item}">
                        <div class="text-link" @click="editItinerary(item)">
                            {{ item.productName }}
                        </div>
                    </template>
                    <template v-slot:item.type="{item}">
                        {{ $t(`itineraryType.${item.type}`) }}
                    </template>
                    <template v-slot:item.obligatoryStatus="{item}">
                        {{ $t(`obligatoryStatus.${item.obligatoryStatus}`) }}
                    </template>
                    <template v-slot:item.action="{item}">
                        <v-btn
                            variant="text"
                            class="text-caption"
                            :data-cy="`delete-${item.id}`"
                            density="compact"
                            @click="deleteItinerary(item.id)"
                        >
                            {{ $t('delete') }}
                        </v-btn>
                    </template>
                </v-data-table-server>
                <ModalWrapper v-model="isShowModal">
                    <template v-slot:title>
                        {{ $t('new_tour_component') }}
                    </template>
                    <template v-slot:content>
                        <v-radio-group v-model="selectedItinerary" density="compact">
                            <v-radio
                                v-for="{value, title} in useItinerary"
                                :key="value"
                                :label="title"
                                :value="value"
                                :data-cy="value"
                                class="pb-1"
                            />
                        </v-radio-group>
                    </template>
                    <template v-slot:btns>
                        <v-btn data-cy="add" variant="flat" @click="addComponent()">
                            {{ $t('add') }}
                        </v-btn>
                    </template>
                </ModalWrapper>
            </template>
            <template v-slot:additional-mode>
                <InfoItineraryForm
                    :contract-id="contractId"
                    :edit-itinerary-id="editItineraryId"
                    :selected-itinerary="selectedItinerary"
                    :entry-code-count="useGetEntryCodeCount"
                    @save="save()"
                    @cancel="modeSwitcher.goToTable()"
                />
            </template>
        </ModeSwitcher>
    </div>
</template>

<script setup lang="ts">
    import InfoItineraryForm from '~/components/products/information/InfoItineraryForm.vue'
    import {toLower} from 'lodash-es'
    import {useFullContract} from '~/composables/form-helpers'

    type HeaderItemType = {
        title: string
        value: string
        width?: string
        minWidth?: string
    }

    const props = defineProps<{
        contractId: number
    }>()

    const isLoading = ref<boolean>(false)
    const itinerary = ref<any[]>([])
    const fullItineraryList = ref<any[]>([])
    const selectedItinerary = ref('ACCOMMODATION')
    const modeSwitcher = ref(null)
    const editItineraryId = ref<number>(null)
    const isShowModal = ref<boolean>(false)
    const duration = ref(useFullContract.value.duration || 1)

    watch(useFullContract, async () => {
        duration.value = useFullContract.value.duration
        itinerary.value = filterItinerary()
    })

    const app = useNuxtApp()
    const {t} = useI18n()

    onMounted(async () => {
        await loadData()
    })

    async function loadData(): Promise<void> {
        try {
            isLoading.value = true
            const res = await app.$api.ownPackageContractsItinerary.get(props.contractId)
            fullItineraryList.value = res.sort((a, b) => a.dayNumber - b.dayNumber)
            itinerary.value = filterItinerary()
        } catch (e) {
            itinerary.value = []
        } finally {
            isLoading.value = false
        }
    }

    const filterItinerary = () => {
        return fullItineraryList.value.filter(({dayNumber}) => dayNumber <= duration.value)
    }

    const save = async (): Promise<void> => {
        await loadData()
        modeSwitcher.value.goToTable()
        editItineraryId.value = null
    }

    const useHeaders: ComputedRef<HeaderItemType[]> = computed(() => {
        const headers: HeaderItemType[] = [
            {title: t('table_day'), value: 'dayNumber', width: '80px'},
            {title: '', value: 'dayAction', width: '100px'},
            {title: t('service_code'), value: 'name', minWidth: '80px'},
            {title: t('name'), value: 'productName'},
            {title: t('service_type'), value: 'type'},
            {title: t('include_component'), value: 'obligatoryStatus'},
            {title: '', value: 'action', minWidth: '120px', width: '10%'},
        ]
        return headers
    })

    const editItinerary = (item): void => {
        modeSwitcher.value.edit()
        editItineraryId.value = item.id
        selectedItinerary.value = item.type
    }

    const deleteItinerary = async (id: number): Promise<void> => {
        try {
            isLoading.value = true
            await app.$api.ownPackageContractsItinerary.remove(props.contractId, id)
            await loadData()
            app.$toast.success(t('success_message.changed_successfully'))
        } catch (e) {
            app.$toast.error(t('error_message.save_error'))
        } finally {
            isLoading.value = false
        }
    }

    const addComponent = () => {
        isShowModal.value = false
        modeSwitcher.value.create()
    }

    const useGetEntryCodeCount = computed(() => {
        return [...itinerary.value].filter(({type}) => type === selectedItinerary.value).length + 1
    })

    const getSameDayIndices = day => {
        return itinerary.value.map((item, index) => (item.dayNumber === day ? index : -1)).filter(index => index !== -1)
    }

    const showDownIcon = index => {
        const currentDay = itinerary.value[index].dayNumber
        const sameDayIndices = getSameDayIndices(currentDay)
        if (sameDayIndices.length === 1) {
            return false
        }
        return index !== sameDayIndices[sameDayIndices.length - 1]
    }
    const showUpIcon = index => {
        const currentDay = itinerary.value[index].dayNumber
        const sameDayIndices = getSameDayIndices(currentDay)
        if (sameDayIndices.length === 1) {
            return false
        }
        return index !== sameDayIndices[0]
    }

    const itineraryDaySwap = async (currentId, nextId) => {
        await app.$api.ownPackageContractsItineraryDaySwap.post(props.contractId, {currentId, nextId})
        await loadData()
    }
</script>
